import { effectiveAnnualUsageKwhForPricing } from '../../../../../helpers/utilityMonthlyDailyToAnnual';

/** Match FinancingGridCard partner labels (excludes Cash for EPC display). */
function normalizeTreeCutPartner(partner: string): string {
    const lower = (partner || '').toLowerCase();
    if (lower.includes('good') && lower.includes('leap')) return 'GoodLeap';
    if (lower.includes('light') && lower.includes('reach')) return 'LightReach';
    if (lower === 'igs' || lower === 'ics') return 'IGS';
    if (lower === 'cash') return 'Cash';
    return partner || 'Unknown';
}

function isStrictSolarProductType(productType: string): boolean {
    const lower = (productType || '').trim().toLowerCase().replace(/\s+/g, '');
    if (!lower) return false;
    // Accept common solar variants from Pricing API (e.g. "Solar", "Solar PPA", "Solar Loan", "S").
    if (lower === 'solar' || lower === 's') return true;
    if (lower.startsWith('solar')) return true;
    return false;
}

function isFailedPricingOffer(offer: any): boolean {
    if (!offer) return true;
    if (offer.__error) return true;
    if (offer.IsSuccessful === false || offer.isSuccessful === false) return true;
    return false;
}

function isRecommendedOffer(offer: any): boolean {
    if (!offer || isFailedPricingOffer(offer)) return false;
    const r = offer.Recommended ?? offer.recommended;
    if (r === true || r === 1) return true;
    if (typeof r === 'string') {
        const s = r.trim().toLowerCase();
        return s === 'true' || s === '1' || s === 'yes';
    }
    return false;
}

/** Recommended Solar offer, else first non-error offer (Pricing API may send Recommended as string). */
function chooseRecommendedSolarOffer(offers: any[]): any | null {
    const list = Array.isArray(offers) ? offers : [];
    const recommended = list.find((o) => isRecommendedOffer(o));
    if (recommended) return recommended;
    return list.find((o) => o && !isFailedPricingOffer(o)) ?? null;
}

/** Resolve utility company code for Pricing API payloads (matches FinancingGridCard). */
export function resolveUtilityCompanyCode(
    base: Record<string, unknown>,
    utilityCompanyNameFromStore: string | null | undefined,
): string {
    const candidates = [
        base.UtilityCompanyCode,
        base.UtilityCompanyName,
        base.UtilityCompany,
        base.UtilityCompanyId,
        utilityCompanyNameFromStore,
    ];
    for (const c of candidates) {
        if (typeof c === 'string' && c.trim() !== '') return c.trim();
        if (typeof c === 'number' && Number.isFinite(c) && c > 0) return String(c);
    }
    return '';
}

/** Cache + cell-state key for one dashboard design at a specific system size, production, and utility. */
export function mainCardPricingScenarioKey(
    cardKey: string,
    estProd: number | undefined,
    systemKw: string | number,
    utilityCompanyCode?: string,
): string | null {
    const systemSize = typeof systemKw === 'number' ? systemKw : Number(systemKw);
    const estProdNum = typeof estProd === 'number' && Number.isFinite(estProd) ? estProd : NaN;
    if (!Number.isFinite(systemSize) || systemSize <= 0 || !Number.isFinite(estProdNum) || estProdNum <= 0) {
        return null;
    }
    const util = (utilityCompanyCode ?? '').trim();
    return `${cardKey}:${systemSize.toFixed(4)}:${Math.round(estProdNum)}:${util}`;
}

/** solarOnlyEpcPayment on recommended Solar offer (several API spellings / nests). */
function solarOnlyEpcPaymentFromOffer(offer: any): number | null {
    if (!offer || offer.__error) return null;
    const read = (o: any): unknown =>
        o?.solarOnlyEpcPayment ??
        o?.SolarOnlyEpcPayment ??
        o?.solar_only_epc_payment;
    let v: unknown = read(offer);
    if (v == null || v === '') v = read(offer?.ExcelOffer);
    if ((v == null || v === '') && offer?.payload != null) {
        const p = offer.payload;
        if (Array.isArray(p) && p[0]) v = read(p[0]) ?? read(p[0]?.ExcelOffer);
        else if (typeof p === 'object') v = read(p) ?? read((p as any).ExcelOffer);
    }
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
        const n = parseFloat(v.replace(/[$,]/g, ''));
        return Number.isFinite(n) ? n : null;
    }
    return null;
}

/**
 * GoodLeap PPA (and similar): recommended offer often exposes purchase price at
 * payload.pricing[0].purchasePrice.value (not solarOnlyEpcPayment / loanAmount).
 */
function goodLeapPayloadPricingPurchasePriceFromOffer(offer: any, systemSizeKw?: number): number | null {
    if (!offer || offer.__error) return null;

    const readFromLine = (line: any): number | null => {
        if (!line || typeof line !== 'object') return null;
        const pp = line.purchasePrice ?? line.PurchasePrice;
        const v = pp?.value ?? pp?.Value;
        if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
        if (typeof v === 'string' && v.trim() !== '') {
            const n = parseFloat(v.replace(/[$,]/g, ''));
            return Number.isFinite(n) && n > 0 ? n : null;
        }
        const ppu = line.pricePerUnit ?? line.PricePerUnit;
        if (ppu && systemSizeKw != null && Number.isFinite(systemSizeKw) && systemSizeKw > 0) {
            const unit = String(ppu.unit ?? ppu.Unit ?? 'W').toUpperCase();
            const rateRaw = ppu.price ?? ppu.Price ?? ppu.value ?? ppu.Value;
            const rate = typeof rateRaw === 'number' ? rateRaw : parseFloat(String(rateRaw ?? '').replace(/[$,]/g, ''));
            if (Number.isFinite(rate) && rate > 0 && (unit === 'W' || unit === '')) {
                return rate * systemSizeKw * 1000;
            }
        }
        return null;
    };

    const pricingFrom = (row: any): any[] | null => {
        if (!row || typeof row !== 'object') return null;
        const pr = row.pricing ?? row.Pricing;
        return Array.isArray(pr) ? pr : null;
    };

    const payload = offer.payload;
    if (Array.isArray(payload)) {
        for (const row of payload) {
            const pr = pricingFrom(row);
            if (!pr) continue;
            for (const line of pr) {
                const n = readFromLine(line);
                if (n != null) return n;
            }
        }
    } else if (payload && typeof payload === 'object') {
        const top = pricingFrom(payload);
        if (top) {
            for (const line of top) {
                const n = readFromLine(line);
                if (n != null) return n;
            }
        }
        for (const val of Object.values(payload)) {
            if (!val || typeof val !== 'object') continue;
            const pr = pricingFrom(val);
            if (!pr) continue;
            for (const line of pr) {
                const n = readFromLine(line);
                if (n != null) return n;
            }
        }
    }
    return null;
}

/** IGS lease/PPA offers often expose per-watt EPC as epcCost (not solarOnlyEpcPayment). */
function epcCostPerWattFromOffer(offer: any): number | null {
    if (!offer || offer.__error) return null;
    const read = (o: any): unknown => o?.epcCost ?? o?.EpcCost ?? o?.epc_cost ?? o?.EPCCost;
    let v: unknown = read(offer);
    if (v == null || v === '') v = read(offer?.ExcelOffer);
    if ((v == null || v === '') && offer?.payload != null) {
        const p = offer.payload;
        if (Array.isArray(p) && p[0]) v = read(p[0]) ?? read(p[0]?.ExcelOffer);
        else if (typeof p === 'object') v = read(p) ?? read((p as any).ExcelOffer);
    }
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
    if (typeof v === 'string' && v.trim() !== '') {
        const n = parseFloat(v.replace(/[$,]/g, ''));
        return Number.isFinite(n) && n > 0 ? n : null;
    }
    return null;
}

/**
 * Convert IGS epcCost to total project EPC. API usually sends $/W (e.g. 2.81); totals are typically 500+.
 */
function igsTotalEpcFromEpcCost(epcCost: number, systemSizeKw?: number): number | null {
    if (!Number.isFinite(epcCost) || epcCost <= 0) return null;
    if (epcCost >= 500) return epcCost;
    if (systemSizeKw != null && Number.isFinite(systemSizeKw) && systemSizeKw > 0) {
        return epcCost * systemSizeKw * 1000;
    }
    return null;
}

/** LightReach card: only offer.totalEpcPayment (recommended PPA) — not solarOnlyEpcPayment. */
function totalEpcPaymentFromOffer(offer: any): number | null {
    if (!offer || offer.__error) return null;
    const read = (o: any): unknown =>
        o?.totalEpcPayment ??
        o?.TotalEpcPayment ??
        o?.TotalEPCPayment ??
        o?.totalEPCPayment ??
        o?.total_epc_payment;
    let v: unknown = read(offer);
    if (v == null || v === '') v = read(offer?.ExcelOffer);
    if ((v == null || v === '') && offer?.payload != null) {
        const p = offer.payload;
        if (Array.isArray(p) && p[0]) v = read(p[0]) ?? read(p[0]?.ExcelOffer);
        else if (typeof p === 'object') v = read(p) ?? read((p as any).ExcelOffer);
    }
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
        const n = parseFloat(v.replace(/[$,]/g, ''));
        return Number.isFinite(n) ? n : null;
    }
    return null;
}

/** GoodLeap Solar loan: payload[*].loanAmount.value (array or { "0": row } object; scan rows). */
function treeCutLoanAmountFromOffer(offer: any): number | null {
    if (offer?.__error) return null;

    const readFromRow = (row: any): number | null => {
        if (!row || typeof row !== 'object') return null;
        const la = row.loanAmount ?? row.LoanAmount;
        const v = la?.value ?? la?.Value;
        if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
        if (typeof v === 'string' && v.trim() !== '') {
            const n = parseFloat(v.replace(/[$,]/g, ''));
            return Number.isFinite(n) && n > 0 ? n : null;
        }
        return null;
    };

    const payload = offer?.payload;
    if (Array.isArray(payload)) {
        for (let i = 0; i < payload.length; i++) {
            const n = readFromRow(payload[i]);
            if (n != null) return n;
        }
    } else if (payload && typeof payload === 'object') {
        const first =
            (payload as any)[0] ??
            (payload as any)['0'];
        const fromFirst = readFromRow(first);
        if (fromFirst != null) return fromFirst;
        for (const row of Object.values(payload)) {
            const n = readFromRow(row);
            if (n != null) return n;
        }
    }

    if (typeof offer?.totalCost === 'number' && Number.isFinite(offer.totalCost) && offer.totalCost > 0) {
        return offer.totalCost;
    }
    return null;
}

/** Shared upfront Solar EPC / purchase extraction (GoodLeap, IGS, LightReach offer shapes). */
function solarUpfrontEpcFromOffer(offer: any, systemSizeKw?: number): number | null {
    if (!offer || isFailedPricingOffer(offer)) return null;
    const purchase = goodLeapPayloadPricingPurchasePriceFromOffer(offer, systemSizeKw);
    if (purchase != null && purchase > 0) return purchase;
    const ppwRaw = offer.ppwRate ?? offer.PpwRate ?? offer.ppw ?? offer.PPW;
    if (systemSizeKw != null && Number.isFinite(systemSizeKw) && systemSizeKw > 0) {
        const ppw = typeof ppwRaw === 'number' ? ppwRaw : parseFloat(String(ppwRaw ?? '').replace(/[$,]/g, ''));
        if (Number.isFinite(ppw) && ppw > 0 && ppw < 50) return ppw * systemSizeKw * 1000;
    }
    const epc = solarOnlyEpcPaymentFromOffer(offer);
    if (epc != null && epc > 0) return epc;
    const totalEpc = totalEpcPaymentFromOffer(offer);
    if (totalEpc != null && totalEpc > 0) return totalEpc;
    const perWatt = epcCostPerWattFromOffer(offer);
    if (perWatt != null) {
        const total = igsTotalEpcFromEpcCost(perWatt, systemSizeKw);
        if (total != null && total > 0) return total;
    }
    return null;
}

/** Same as FinancingGridCard goodLeapPurchasePriceFromOffer — PPA uses payload.pricing[].purchasePrice.value when present, else solar-only EPC / loan. */
function goodLeapPurchasePriceFromOffer(
    offer: any,
    financeMethodRaw: string,
    systemSizeKw?: number,
): number | null {
    if (!offer || offer.__error) return null;
    const m = (financeMethodRaw || '').toUpperCase().replace(/\s+/g, '');
    if (m === 'CASH') {
        if (typeof offer.totalCost === 'number' && Number.isFinite(offer.totalCost) && offer.totalCost > 0) {
            return offer.totalCost;
        }
        return null;
    }
    if (m === 'LOAN') {
        return treeCutLoanAmountFromOffer(offer);
    }
    if (m === 'PPA' || m === 'LEASE' || m.startsWith('LEASE')) {
        const upfront = solarUpfrontEpcFromOffer(offer, systemSizeKw);
        if (upfront != null && upfront > 0) return upfront;
        const loan = treeCutLoanAmountFromOffer(offer);
        return loan != null && loan > 0 ? loan : null;
    }
    const loan = treeCutLoanAmountFromOffer(offer);
    if (loan != null && loan > 0) return loan;
    const upfront = solarUpfrontEpcFromOffer(offer, systemSizeKw);
    if (upfront != null && upfront > 0) return upfront;
    if (typeof offer.totalCost === 'number' && Number.isFinite(offer.totalCost) && offer.totalCost > 0) {
        return offer.totalCost;
    }
    return null;
}

export function formatTreeCutMoney(n: number): string {
    const rounded = Math.round(n);
    if (Math.abs(n - rounded) < 1e-6) return rounded.toLocaleString();
    return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

/** Fixed column order in tree-cutting pricing tables (matches financing grid partners). */
export const TREE_CUT_PRICING_PARTNER_COLUMNS = ['GoodLeap', 'IGS', 'LightReach', 'Cash'] as const;
export type TreeCutPricingPartnerColumn = (typeof TREE_CUT_PRICING_PARTNER_COLUMNS)[number];

function totalCostFromOffer(offer: any): number | null {
    if (!offer || offer.__error) return null;
    if (typeof offer.totalCost === 'number' && Number.isFinite(offer.totalCost) && offer.totalCost > 0) {
        return offer.totalCost;
    }
    return null;
}

function formatFinanceMethodLabel(methodRaw: string): string {
    const u = (methodRaw || '').toUpperCase();
    if (u === 'LOAN') return 'Loan';
    if (u === 'LEASE') return 'Lease';
    if (u === 'PPA') return 'PPA';
    if (u === 'CASH') return 'Cash';
    return methodRaw || 'Offer';
}

function excelTermAprLine(offer: any): string {
    const ex = offer?.ExcelOffer;
    if (!ex) return '';
    const term = typeof ex.term === 'number' && ex.term > 0 ? `${ex.term} yr` : '';
    let apr = ex.apr ?? ex.Apr;
    if (typeof apr === 'number' && apr > 0 && apr < 1) apr = apr * 100;
    const aprStr = typeof apr === 'number' && apr > 0 ? `${apr.toFixed(2)}% APR` : '';
    return [term, aprStr].filter(Boolean).join(' · ');
}

/** First-year style monthly for PPA/Lease (LightReach monthlyPayments or GoodLeap lease payload). */
function firstYearMonthlyFromTreeCutOffer(offer: any): number | null {
    if (!offer || offer.__error) return null;
    const mp = offer.monthlyPayments?.[0] ?? offer.MonthlyPayments?.[0];
    const v =
        mp?.totalMonthlyPayment ??
        mp?.TotalMonthlyPayment ??
        mp?.monthlyPayment ??
        mp?.MonthlyPayment;
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
    const payload = offer.payload;
    if (payload && !Array.isArray(payload)) {
        const fp = payload.pricing?.[0] ?? payload.Pricing?.[0];
        const fy = fp?.firstYearMonthlyPayment?.value ?? fp?.FirstYearMonthlyPayment?.Value;
        if (typeof fy === 'number' && fy > 0) return fy;
    }
    if (Array.isArray(payload) && payload[0]) {
        const p0 = payload[0];
        const calc = p0?.calculations?.[0] ?? p0?.Calculations?.[0];
        const rounded = calc?.roundedAmount?.value ?? calc?.RoundedAmount?.Value;
        const amt = calc?.amount?.value ?? calc?.Amount?.Value;
        if (typeof rounded === 'number' && rounded > 0) return rounded;
        if (typeof amt === 'number' && amt > 0) return amt;
    }
    const est = offer?.estimatedMonthlyPayment ?? offer?.EstimatedMonthlyPayment;
    if (typeof est === 'number' && est > 0) return est;
    return null;
}

/**
 * One UI block per API offer under strict SOLAR + FinanceMethod (Loan amount, PPA/Lease EPC + monthly, Cash total).
 */
function formatTreeCutSolarOfferBlock(pricingItem: any, offer: any): string | null {
    if (!offer || offer.__error) return null;
    const methodU = String(pricingItem.FinanceMethod || '').toUpperCase();
    const methodLabel = formatFinanceMethodLabel(pricingItem.FinanceMethod || '');
    const productName = String(offer?.name || '').trim();
    const meta = excelTermAprLine(offer);
    const head = [methodLabel, meta].filter(Boolean).join(' · ');

    const lines: string[] = [];

    if (methodU === 'CASH') {
        const tc = totalCostFromOffer(offer);
        if (tc == null) return null;
        lines.push(head || methodLabel);
        if (productName) lines.push(productName);
        lines.push(`Total $${formatTreeCutMoney(tc)}`);
        return lines.join('\n');
    }

    if (methodU === 'LOAN') {
        const loan = treeCutLoanAmountFromOffer(offer);
        if (loan == null || loan <= 0) return null;
        lines.push(head || methodLabel);
        if (productName) lines.push(productName);
        lines.push(`$${formatTreeCutMoney(loan)}`);
        return lines.join('\n');
    }

    if (methodU === 'PPA' || methodU === 'LEASE') {
        const purchase = goodLeapPayloadPricingPurchasePriceFromOffer(offer, systemSizeKw);
        const epc = solarOnlyEpcPaymentFromOffer(offer);
        const mo = firstYearMonthlyFromTreeCutOffer(offer);
        const bits: string[] = [];
        if (purchase != null && purchase > 0) bits.push(`$${formatTreeCutMoney(purchase)}`);
        else if (epc != null && epc > 0) bits.push(`EPC $${formatTreeCutMoney(epc)}`);
        if (mo != null && mo > 0) bits.push(`$${formatTreeCutMoney(mo)}/mo`);
        if (bits.length === 0) return null;
        lines.push(head || methodLabel);
        if (productName) lines.push(productName);
        lines.push(bits.join(' · '));
        return lines.join('\n');
    }

    const loan = treeCutLoanAmountFromOffer(offer);
    if (loan != null && loan > 0) {
        lines.push(head || methodLabel);
        if (productName) lines.push(productName);
        lines.push(`$${formatTreeCutMoney(loan)}`);
        return lines.join('\n');
    }
    const epc = solarOnlyEpcPaymentFromOffer(offer);
    if (epc != null && epc > 0) {
        lines.push(head || methodLabel);
        if (productName) lines.push(productName);
        lines.push(`EPC $${formatTreeCutMoney(epc)}`);
        return lines.join('\n');
    }
    const tc = totalCostFromOffer(offer);
    if (tc != null && tc > 0) {
        lines.push(head || methodLabel);
        if (productName) lines.push(productName);
        lines.push(`Total $${formatTreeCutMoney(tc)}`);
        return lines.join('\n');
    }
    return null;
}

function findGoodLeapStrictSolarPricingItem(
    partnerBlock: any,
    want: 'PPA' | 'LEASE' | 'LOAN',
): any | null {
    for (const pi of partnerBlock.pricing || []) {
        if (!isStrictSolarProductType(pi.ProductType || '')) continue;
        const m = String(pi.FinanceMethod || '')
            .toUpperCase()
            .replace(/\s+/g, '');
        if (want === 'PPA' && m === 'PPA') return pi;
        if (want === 'LEASE' && (m === 'LEASE' || m.startsWith('LEASE'))) return pi;
        if (want === 'LOAN' && m === 'LOAN') return pi;
    }
    return null;
}

/** One display line from the recommended offer for that pricing row (PPA purchase price, Lease EPC/mo, Loan amount). */
function goodLeapTreeCutLineFromRecommendedOffer(
    pricingItem: any,
    recommended: any,
    systemSizeKw?: number,
): string | null {
    const methodU = String(pricingItem.FinanceMethod || '')
        .toUpperCase()
        .replace(/\s+/g, '');
    if (methodU === 'PPA') {
        const pp = goodLeapPurchasePriceFromOffer(recommended, pricingItem.FinanceMethod || 'PPA', systemSizeKw);
        if (pp != null && pp > 0) return `$${formatTreeCutMoney(pp)}`;
        return null;
    }
    if (methodU === 'LEASE' || methodU.startsWith('LEASE')) {
        const upfront = solarUpfrontEpcFromOffer(recommended, systemSizeKw);
        const mo = firstYearMonthlyFromTreeCutOffer(recommended);
        const bits: string[] = [];
        if (upfront != null && upfront > 0) bits.push(`$${formatTreeCutMoney(upfront)}`);
        if (mo != null && mo > 0) bits.push(`$${formatTreeCutMoney(mo)}/mo`);
        if (bits.length === 0) return null;
        return bits.join(' · ');
    }
    if (methodU === 'LOAN') {
        const loan = treeCutLoanAmountFromOffer(recommended);
        if (loan != null && loan > 0) return `$${formatTreeCutMoney(loan)}`;
        return null;
    }
    return null;
}

/**
 * GoodLeap tree-cut card: strict Solar, recommended offer only — PPA, then Loan (payload loanAmount.value), then Lease.
 * Loan before Lease so a lease monthly line does not hide the recommended loan principal.
 */
function goodLeapRecommendedSolarFinancingBlocks(partnerBlock: any, systemSizeKw?: number): string[] {
    for (const want of ['PPA', 'LOAN', 'LEASE'] as const) {
        const pricingItem = findGoodLeapStrictSolarPricingItem(partnerBlock, want);
        if (!pricingItem) continue;
        const offers = pricingItem.Offer || [];
        const chosen = chooseRecommendedSolarOffer(offers);
        if (!chosen) continue;
        const line = goodLeapTreeCutLineFromRecommendedOffer(pricingItem, chosen, systemSizeKw);
        if (line) return [line];
    }
    return [];
}

/** LightReach tree-cut card: recommended Solar + PPA offer’s totalEpcPayment only. */
function lightReachRecommendedPpaTotalEpcPaymentBlocks(partnerBlock: any): string[] {
    for (const pricingItem of partnerBlock.pricing || []) {
        if (!isStrictSolarProductType(pricingItem.ProductType || '')) continue;
        if (String(pricingItem.FinanceMethod || '').toUpperCase() !== 'PPA') continue;
        const offers = pricingItem.Offer || [];
        const chosen = chooseRecommendedSolarOffer(offers);
        if (!chosen) continue;
        const total = totalEpcPaymentFromOffer(chosen);
        if (total != null && total > 0) {
            return [`$${formatTreeCutMoney(total)}`];
        }
    }
    return [];
}

/**
 * IGS card value: upfront EPC / purchase / loan only (same family as GoodLeap + LightReach).
 * Never use monthly/EMI — that belongs in the financing grid, not the AI design summary column.
 */
function igsPrimaryValueFromOffer(
    offer: any,
    financeMethodRaw?: string,
    systemSizeKw?: number,
): string | null {
    if (!offer || offer.__error) return null;
    const m = String(financeMethodRaw || '')
        .toUpperCase()
        .replace(/\s+/g, '');

    const upfrontFromOffer = (): string | null => {
        const purchase = goodLeapPayloadPricingPurchasePriceFromOffer(offer, systemSizeKw);
        if (purchase != null && purchase > 0) return `$${formatTreeCutMoney(purchase)}`;
        const totalEpc = totalEpcPaymentFromOffer(offer);
        if (totalEpc != null && totalEpc > 0) return `$${formatTreeCutMoney(totalEpc)}`;
        const solarEpc = solarOnlyEpcPaymentFromOffer(offer);
        if (solarEpc != null && solarEpc > 0) return `$${formatTreeCutMoney(solarEpc)}`;
        const perWatt = epcCostPerWattFromOffer(offer);
        if (perWatt != null) {
            const total = igsTotalEpcFromEpcCost(perWatt, systemSizeKw);
            if (total != null && total > 0) return `$${formatTreeCutMoney(total)}`;
        }
        return null;
    };

    if (m === 'CASH') {
        const tc = totalCostFromOffer(offer);
        return tc != null && tc > 0 ? `$${formatTreeCutMoney(tc)}` : null;
    }
    if (m === 'LOAN') {
        const loan = treeCutLoanAmountFromOffer(offer);
        return loan != null && loan > 0 ? `$${formatTreeCutMoney(loan)}` : null;
    }
    if (m === 'PPA' || m === 'LEASE' || m.startsWith('LEASE')) {
        return upfrontFromOffer();
    }

    const loan = treeCutLoanAmountFromOffer(offer);
    if (loan != null && loan > 0) return `$${formatTreeCutMoney(loan)}`;
    const upfront = upfrontFromOffer();
    if (upfront) return upfront;
    const tc = totalCostFromOffer(offer);
    if (tc != null && tc > 0) return `$${formatTreeCutMoney(tc)}`;
    return null;
}

/**
 * IGS card: strict Solar, recommended offer — PPA, then Loan, then Lease (same order as GoodLeap).
 */
function igsRecommendedSolarFinancingBlocks(partnerBlock: any, systemSizeKw?: number): string[] {
    for (const want of ['PPA', 'LOAN', 'LEASE'] as const) {
        const pricingItem = findGoodLeapStrictSolarPricingItem(partnerBlock, want);
        if (!pricingItem) continue;
        const offers = pricingItem.Offer || [];
        const chosen = chooseRecommendedSolarOffer(offers);
        if (!chosen) continue;
        const line = igsPrimaryValueFromOffer(chosen, pricingItem.FinanceMethod || '', systemSizeKw);
        if (line) return [line];
    }
    return [];
}

/** Partners shown in the AI design EPC comparison modal (excludes Cash). */
export const EPC_DISPLAY_PARTNERS = ['GoodLeap', 'IGS', 'LightReach'] as const;
export type EpcDisplayPartner = (typeof EPC_DISPLAY_PARTNERS)[number];

/** Optional context when parsing pricing API bodies (e.g. system kW for IGS epcCost $/W). */
export type PricingDisplayContext = { systemSizeKw?: number };

export type DesignEpcCell =
    | { status: 'ok'; amount: number; formatted: string }
    | { status: 'none' };

function designEpcCellFromAmount(amount: number | null | undefined): DesignEpcCell {
    if (amount == null || !Number.isFinite(amount) || amount <= 0) return { status: 'none' };
    return { status: 'ok', amount, formatted: `$${formatTreeCutMoney(amount)}` };
}

function goodLeapRecommendedSolarEpcAmount(partnerBlock: any, systemSizeKw?: number): number | null {
    for (const want of ['PPA', 'LOAN', 'LEASE'] as const) {
        const pricingItem = findGoodLeapStrictSolarPricingItem(partnerBlock, want);
        if (!pricingItem) continue;
        const offers = pricingItem.Offer || [];
        const chosen = chooseRecommendedSolarOffer(offers);
        if (!chosen) continue;
        const amount = goodLeapPurchasePriceFromOffer(chosen, pricingItem.FinanceMethod || '', systemSizeKw);
        if (amount != null && amount > 0) return amount;
    }
    return null;
}

function igsPrimaryEpcAmountFromOffer(
    offer: any,
    financeMethodRaw?: string,
    systemSizeKw?: number,
): number | null {
    if (!offer || offer.__error) return null;
    const m = String(financeMethodRaw || '')
        .toUpperCase()
        .replace(/\s+/g, '');

    const upfrontAmount = (): number | null => {
        const purchase = goodLeapPayloadPricingPurchasePriceFromOffer(offer, systemSizeKw);
        if (purchase != null && purchase > 0) return purchase;
        const totalEpc = totalEpcPaymentFromOffer(offer);
        if (totalEpc != null && totalEpc > 0) return totalEpc;
        const solarEpc = solarOnlyEpcPaymentFromOffer(offer);
        if (solarEpc != null && solarEpc > 0) return solarEpc;
        const perWatt = epcCostPerWattFromOffer(offer);
        if (perWatt != null) {
            const total = igsTotalEpcFromEpcCost(perWatt, systemSizeKw);
            if (total != null && total > 0) return total;
        }
        return null;
    };

    if (m === 'CASH') return totalCostFromOffer(offer);
    if (m === 'LOAN') return treeCutLoanAmountFromOffer(offer);
    if (m === 'PPA' || m === 'LEASE' || m.startsWith('LEASE')) return upfrontAmount();

    const loan = treeCutLoanAmountFromOffer(offer);
    if (loan != null && loan > 0) return loan;
    const upfront = upfrontAmount();
    if (upfront != null && upfront > 0) return upfront;
    return totalCostFromOffer(offer);
}

function igsRecommendedSolarEpcAmount(partnerBlock: any, systemSizeKw?: number): number | null {
    for (const want of ['PPA', 'LOAN', 'LEASE'] as const) {
        const pricingItem = findGoodLeapStrictSolarPricingItem(partnerBlock, want);
        if (!pricingItem) continue;
        const offers = pricingItem.Offer || [];
        const chosen = chooseRecommendedSolarOffer(offers);
        if (!chosen) continue;
        const amount = igsPrimaryEpcAmountFromOffer(chosen, pricingItem.FinanceMethod || '', systemSizeKw);
        if (amount != null && amount > 0) return amount;
    }
    return null;
}

function lightReachRecommendedPpaTotalEpcAmount(partnerBlock: any): number | null {
    for (const pricingItem of partnerBlock.pricing || []) {
        if (!isStrictSolarProductType(pricingItem.ProductType || '')) continue;
        if (String(pricingItem.FinanceMethod || '').toUpperCase() !== 'PPA') continue;
        const offers = pricingItem.Offer || [];
        const chosen = chooseRecommendedSolarOffer(offers);
        if (!chosen) continue;
        const total = totalEpcPaymentFromOffer(chosen);
        if (total != null && total > 0) return total;
    }
    return null;
}

/**
 * Recommended Solar offer EPC / purchase per partner (GoodLeap & IGS: solar EPC or purchase for PPA/LEASE;
 * LightReach: totalEpcPayment on recommended PPA). Same offer selection as tree-cut card builders.
 */
function offerErrorMessage(offer: any): string | null {
    if (!offer) return null;

    const directMsg = offer.Message ?? offer.message;
    if (typeof directMsg === 'string' && directMsg.trim()) return directMsg.trim();

    const err = offer.__error ?? offer.Error ?? offer.error;
    if (typeof err === 'string' && err.trim()) return err.trim();
    if (err && typeof err === 'object') {
        const msg = (err as { message?: string; Message?: string; detail?: string; Detail?: string }).message
            ?? (err as { Message?: string }).Message
            ?? (err as { detail?: string }).detail
            ?? (err as { Detail?: string }).Detail;
        if (typeof msg === 'string' && msg.trim()) return msg.trim();
    }

    if (offer.IsSuccessful === false || offer.isSuccessful === false) {
        const fallback = offer.Reason ?? offer.reason ?? offer.Detail ?? offer.detail;
        if (typeof fallback === 'string' && fallback.trim()) return fallback.trim();
    }

    return null;
}

function partnerSolarPricingUnavailableDetail(partnerBlock: any): string | null {
    for (const pricingItem of partnerBlock.pricing || []) {
        if (!isStrictSolarProductType(pricingItem.ProductType || '')) continue;
        for (const offer of pricingItem.Offer || []) {
            const err = offerErrorMessage(offer);
            if (err) return err;
        }
    }
    return null;
}

function partnerSolarEpcMissingReason(partnerBlock: any, partner: EpcDisplayPartner): string | null {
    const solarItems = (partnerBlock.pricing || []).filter((pi: any) =>
        isStrictSolarProductType(pi.ProductType || ''),
    );
    if (solarItems.length === 0) {
        return `${partner} did not return Solar (PPA, Loan, or Lease) pricing for this system size.`;
    }
    const solarFinance = solarItems.filter((pi: any) => {
        const m = String(pi.FinanceMethod || '')
            .toUpperCase()
            .replace(/\s+/g, '');
        return m === 'PPA' || m === 'LOAN' || m.startsWith('LEASE');
    });
    if (solarFinance.length === 0) {
        return `${partner} returned Solar rows but none with PPA, Loan, or Lease for this system size.`;
    }
    const offerCount = solarFinance.reduce((n: number, pi: any) => n + (pi.Offer || []).length, 0);
    if (offerCount === 0) {
        return `${partner} Solar pricing rows have no offers for this system size.`;
    }
    return `${partner} returned Solar offers but no upfront EPC / purchase price for this system size.`;
}

/** Pricing API offer failure text (`Message`, `__error`, `IsSuccessful: false`) for N/A tooltips. */
export function partnerEpcUnavailableDetailFromApi(
    apiBody: any,
    partner: EpcDisplayPartner,
    fallback: string,
): string {
    const raw = apiBody?.data ?? apiBody;
    if (!Array.isArray(raw)) return fallback;
    for (const block of raw) {
        const p = normalizeTreeCutPartner(block.FinancePartner || '');
        if (p === partner) {
            const apiErr = partnerSolarPricingUnavailableDetail(block);
            if (apiErr) return apiErr;
            return partnerSolarEpcMissingReason(block, partner) ?? fallback;
        }
    }
    return `${partner} was not included in the pricing API response.`;
}

export function buildDesignEpcByPartner(
    apiBody: any,
    ctx?: PricingDisplayContext,
): Record<EpcDisplayPartner, DesignEpcCell> {
    const systemSizeKw = ctx?.systemSizeKw;
    const empty: Record<EpcDisplayPartner, DesignEpcCell> = {
        GoodLeap: { status: 'none' },
        IGS: { status: 'none' },
        LightReach: { status: 'none' },
    };
    const raw = apiBody?.data ?? apiBody;
    if (!Array.isArray(raw) || raw.length === 0) return empty;

    for (const block of raw) {
        const partner = normalizeTreeCutPartner(block.FinancePartner || '');
        if (partner === 'GoodLeap') {
            empty.GoodLeap = designEpcCellFromAmount(goodLeapRecommendedSolarEpcAmount(block, systemSizeKw));
        } else if (partner === 'LightReach') {
            empty.LightReach = designEpcCellFromAmount(lightReachRecommendedPpaTotalEpcAmount(block));
        } else if (partner === 'IGS') {
            empty.IGS = designEpcCellFromAmount(igsRecommendedSolarEpcAmount(block, systemSizeKw));
        }
    }
    return empty;
}

/** One Pricing API response → all strict-Solar offers per partner (order preserved). GoodLeap: PPA → Loan → Lease; LightReach: PPA totalEpcPayment. */
export function buildTreeCutPartnerColumnCells(
    apiBody: any,
    ctx?: PricingDisplayContext,
): Record<TreeCutPricingPartnerColumn, string[]> {
    const systemSizeKw = ctx?.systemSizeKw;
    const cells: Record<TreeCutPricingPartnerColumn, string[]> = {
        GoodLeap: [],
        IGS: [],
        LightReach: [],
        Cash: [],
    };
    const raw = apiBody?.data ?? apiBody;
    if (!Array.isArray(raw) || raw.length === 0) return cells;

    for (const block of raw) {
        const partner = normalizeTreeCutPartner(block.FinancePartner || '');
        if (!(TREE_CUT_PRICING_PARTNER_COLUMNS as readonly string[]).includes(partner)) continue;

        if (partner === 'GoodLeap') {
            cells.GoodLeap = goodLeapRecommendedSolarFinancingBlocks(block, systemSizeKw);
            continue;
        }
        if (partner === 'LightReach') {
            cells.LightReach = lightReachRecommendedPpaTotalEpcPaymentBlocks(block);
            continue;
        }
        if (partner === 'IGS') {
            cells.IGS = igsRecommendedSolarFinancingBlocks(block, systemSizeKw);
            continue;
        }

        const blocks: string[] = [];
        for (const pricingItem of block.pricing || []) {
            if (!isStrictSolarProductType(pricingItem.ProductType || '')) continue;
            for (const offer of pricingItem.Offer || []) {
                const b = formatTreeCutSolarOfferBlock(pricingItem, offer);
                if (b) blocks.push(b);
            }
        }
        cells[partner as TreeCutPricingPartnerColumn] = blocks;
    }
    return cells;
}

export type TreeCutPricingTable = 'single' | 'multi';

/** Dashboard catalog card key for a single-tree cut row (`DashboardAiRailTreeCuttingPanel`). */
export function treeCutDashboardCardKey(table: TreeCutPricingTable, scenarioRowIndex: number): string {
    return table === 'single' ? `tree-single-${scenarioRowIndex}` : `tree-multi-${scenarioRowIndex}`;
}

/** Parse {@link treeCutDashboardCardKey} back to table + row index. */
export function parseTreeCutDashboardCardKey(
    cardKey: string,
): { table: TreeCutPricingTable; scenarioRowIndex: number } | null {
    const single = /^tree-single-(\d+)$/.exec(cardKey);
    if (single) return { table: 'single', scenarioRowIndex: Number(single[1]) };
    const multi = /^tree-multi-(\d+)$/.exec(cardKey);
    if (multi) return { table: 'multi', scenarioRowIndex: Number(multi[1]) };
    return null;
}

export function treeCutPartnerCellStateKey(
    table: TreeCutPricingTable,
    scenarioRowIndex: number,
    partner: TreeCutPricingPartnerColumn,
): string {
    return `${table}:${scenarioRowIndex}:${partner}`;
}

export type TreeCutPartnerCellState =
    | { status: 'loading' }
    | { status: 'ok'; blocks: string[] }
    | { status: 'error'; errorDetail: string };

export type MainCardPartnerCellState =
    | { status: 'loading' }
    | { status: 'ok'; blocks: string[] }
    | { status: 'error'; errorDetail: string };

/**
 * One cache entry + one Pricing API call per tree table row (not deduped across rows).
 * Max EPC / loan are computed only inside that row's response body — never merged across rows or calls.
 */
export function treeCutScenarioPricingParamKey(
    scenario: { actualWattage?: number },
    table: TreeCutPricingTable,
    scenarioRowIndex: number,
    totalModulesBase: number | undefined,
    modulePower: number,
    utilityCompanyCode?: string,
): string | null {
    const modulesNumeric =
        typeof totalModulesBase === 'number' && Number.isFinite(totalModulesBase) ? totalModulesBase : undefined;
    let systemSize = NaN;
    if (modulesNumeric != null && modulePower > 0) systemSize = (modulesNumeric * modulePower) / 1000;
    else if (typeof scenario.actualWattage === 'number') systemSize = scenario.actualWattage / 1000;
    const estProd = typeof scenario.actualWattage === 'number' ? scenario.actualWattage : NaN;
    if (!Number.isFinite(systemSize) || !Number.isFinite(estProd) || systemSize <= 0) return null;
    const util = (utilityCompanyCode ?? '').trim();
    return `${table}:${scenarioRowIndex}:${systemSize.toFixed(4)}_${estProd.toFixed(4)}:${util}`;
}

/**
 * Annual kWh for the financing pricing modal for a tree-cut row (same scaling as lazy partner pricing).
 */
export function annualKwhEstimateForTreeCutFinancingModal(args: {
    solarUtilityData: unknown;
    dashboardAnnualUsage: number | null | undefined;
    SFannualUsage: unknown;
    treeCutScenarioBaseWattage?: number;
    scenarioDcWatts: number | undefined;
}): number {
    const base = (args.solarUtilityData as { SolarUtility?: Record<string, unknown> } | null | undefined)?.SolarUtility;
    const fromDashboard =
        typeof args.dashboardAnnualUsage === 'number' &&
        Number.isFinite(args.dashboardAnnualUsage) &&
        args.dashboardAnnualUsage > 0
            ? Math.round(args.dashboardAnnualUsage)
            : null;
    const utilityAnnualKwh =
        fromDashboard ??
        effectiveAnnualUsageKwhForPricing(base, args.SFannualUsage as number | null | undefined);
    if (utilityAnnualKwh == null || !Number.isFinite(utilityAnnualKwh) || utilityAnnualKwh <= 0) return 0;

    const baseW =
        typeof args.treeCutScenarioBaseWattage === 'number' &&
        Number.isFinite(args.treeCutScenarioBaseWattage) &&
        args.treeCutScenarioBaseWattage > 0
            ? args.treeCutScenarioBaseWattage
            : null;
    const scenW =
        typeof args.scenarioDcWatts === 'number' &&
        Number.isFinite(args.scenarioDcWatts) &&
        args.scenarioDcWatts > 0
            ? args.scenarioDcWatts
            : null;
    if (baseW != null && scenW != null) {
        const ratio = Math.min(2, Math.max(0.25, scenW / baseW));
        return Math.round(utilityAnnualKwh * ratio);
    }
    return Math.round(utilityAnnualKwh);
}
